import { API_PROXY_PREFIX } from './devFlags';
import { tokens } from './designTokens';
import type { FilterField, Option, SeriesColumn } from './interfaces/Dashboard';

export const API_GATEWAY_URL = import.meta.env.VITE_API_GATEWAY_URL;

/**
 * Base every relative API path is resolved against.
 *
 * While the Vite dev server is running with a proxy configured this is a
 * relative prefix (e.g. `/gateway`), which makes calls same-origin and so
 * sidesteps the CORS restriction on the gateway. In any build
 * `API_PROXY_PREFIX` is an empty string, so the absolute gateway URL is
 * used — see the `command === 'serve'` gate in vite.config.ts.
 */
export const API_BASE_URL = API_PROXY_PREFIX || API_GATEWAY_URL;

export const LOGIN_PAGE_LOGO = '/mro-corp-logo.svg';

/** API endpoint paths, driven by the active `.env.<mode>` file. */
export const ENDPOINTS = {
  FETCH_ALL_REQUEST_COUNT: import.meta.env
    .VITE_FETCH_ALL_REQUEST_COUNT_ENDPOINT,
  FETCH_ALL_HEALTH_SYSTEMS: import.meta.env
    .VITE_FETCH_ALL_HEALTH_SYSTEMS_ENDPOINT,
  FETCH_HEALTH_SYSTEM_SUMMARY_COUNT: import.meta.env
    .VITE_FETCH_HEALTH_SYSTEM_SUMMARY_COUNT_ENDPOINT,
  FETCH_ALL_REQUESTS: import.meta.env.VITE_FETCH_ALL_REQUESTS_ENDPOINT,
  DOWNLOAD_PDF_SIGNED_URL: import.meta.env
    .VITE_DOWNLOAD_PDF_SIGNED_URL_ENDPOINT,
  FETCH_JOB_SUMMARY: import.meta.env.VITE_FETCH_JOB_SUMMARY_ENDPOINT,
  FETCH_ALL_PURPOSE_OF_USES: import.meta.env
    .VITE_FETCH_ALL_PURPOSE_OF_USES_ENDPOINT,
} as const;

/** Kept in step with the tab labels in Header.tsx - same rename, same reason. */
export const ALL_ROUTES = {
  LOGIN: '/',
  LANDING_PAGE: '/summary-overview',
  HEALTH_SYSTEMWISE_SUMMARY_PAGE: '/health-system-breakdown',
  DASHBOARD: '/request-details',
} as const;

/**
 * Backend `filestatus` ids grouped by the label shown in the UI.
 * Carried over verbatim from the legacy app — the ids are assigned by the
 * connector, so do not renumber them.
 */
export const FILE_STATUS: Record<string, number[]> = {
  Inprogress: [3, 12, 14, 4],
  Inqueue: [1],
  'Patient Not Found': [16],
  'Multiple Patients Found': [21],
  'Encounter Not Found': [22],
  'Document Not Found': [23],
  Processed: [5, 24],
  Failed: [18, 2],
  'FYI Flag Found': [25],
  'Patient Mismatch': [27],
  'Manual Processing Required': [28],
};

/**
 * The status series rendered in every chart, table column and CSV export.
 * `field` is the key the API returns on each summary row, so the order here
 * drives column order across the whole app.
 */
export const seriesColumnList: SeriesColumn[] = [
  {
    label: 'Patient Not Found',
    value: 'Patient Not Found',
    field: 'Patient not Found',
    name: 'Patient not Found',
    color: tokens.status.patientNotFound,
  },
  {
    label: 'Multiple Patients Found',
    value: 'Multiple Patients Found',
    field: 'Multiple Patients Found',
    name: 'Multiple Patients Found',
    color: tokens.status.multiplePatientsFound,
  },
  {
    label: 'Encounter Not Found',
    value: 'Encounter Not Found',
    field: 'Encounter not Found',
    name: 'Encounter not Found',
    color: tokens.status.encounterNotFound,
  },
  {
    label: 'Document Not Found',
    value: 'Document Not Found',
    field: 'Document not Found',
    name: 'Document not Found',
    color: tokens.status.documentNotFound,
  },
  {
    label: 'FYI Flag Found',
    value: 'FYI Flag Found',
    field: 'FYI Flag Found',
    name: 'FYI Flag Found',
    color: tokens.status.fyiFlagFound,
  },
  {
    label: 'Failed',
    value: 'Failed',
    field: 'Failed',
    name: 'Failed',
    color: tokens.status.failed,
  },
  {
    label: 'Processed',
    value: 'Processed',
    field: 'Processed',
    name: 'Processed',
    color: tokens.status.processed,
  },
  {
    label: 'In queue',
    value: 'Inqueue',
    field: 'In queue',
    name: 'In queue',
    color: tokens.status.inQueue,
  },
  {
    label: 'In progress',
    value: 'Inprogress',
    field: 'In progress',
    name: 'In progress',
    color: tokens.status.inProgress,
  },
  {
    label: 'Patient Mismatch',
    value: 'Patient Mismatch',
    field: 'Patient Mismatch',
    name: 'Patient Mismatch',
    color: tokens.status.patientMismatch,
  },
  {
    label: 'Manual Processing Required',
    value: 'Manual Processing Required',
    field: 'Manual Processing Required',
    name: 'Manual Processing Required',
    color: tokens.status.manualProcessingRequired,
  },
];

export const DATE_FILTERS: Option[] = [
  { label: 'All', value: 'All' },
  { label: '30 Min', value: '30_Min' },
  { label: '60 Min', value: '60_Min' },
  { label: '8 Hours', value: '8_Hours' },
  { label: '12 Hours', value: '12_Hours' },
  { label: 'Today', value: 'Today' },
  { label: '4 Days', value: 'Four_Days' },
  { label: '7 Days', value: 'Seven_Days' },
  { label: 'Custom Date', value: 'Custom' },
];

export const FILTER_TYPES: Option[] = [
  { label: 'EHR', value: 'EHR_WISE' },
  { label: 'Purpose of use', value: 'PURPOSE_OF_USE_WISE' },
  { label: 'Facility', value: 'FACILITY_WISE' },
  { label: 'Location', value: 'LOCATION_WISE' },
];

/** X-axis caption per filter type, used by the summary column charts. */
export const FILTER_TYPE_AXIS_TITLES: Record<string, string> = {
  PURPOSE_OF_USE_WISE: 'Purpose of uses',
  EHR_WISE: 'EHRs',
  LOCATION_WISE: 'Health System Locations',
  FACILITY_WISE: 'Health Systems',
};

/** Search fields rendered above the Dashboard request grid. */
export const filterFields: FilterField[] = [
  {
    label: 'ROI Request ID',
    value: '',
    responseValue: 'requesterrefid',
    id: 'requesterrefid',
    type: 'string',
    md: 2,
    xs: 12,
  },
  {
    label: 'Vendor Request ID',
    value: '',
    responseValue: 'vendorrequestid',
    id: 'vendorrequestid',
    type: 'string',
    md: 2,
    xs: 12,
  },
  {
    label: 'Request ID',
    value: '',
    responseValue: 'requestid',
    id: 'requestid',
    type: 'string',
    md: 2,
    xs: 12,
  },
  {
    label: 'From Date',
    value: '',
    responseValue: 'fromdate',
    id: 'fromdate',
    type: 'date',
    md: 2,
    xs: 12,
  },
  {
    label: 'To Date',
    value: '',
    responseValue: 'todate',
    id: 'todate',
    type: 'date',
    md: 2,
    xs: 12,
  },
  {
    label: 'Select Status',
    value: null,
    responseValue: 'filestatus',
    id: 'filestatus',
    type: 'select',
    options: seriesColumnList,
    isMultiple: false,
    md: 2,
    xs: 12,
  },
  {
    label: 'Select Purpose of Use',
    value: null,
    responseValue: 'purposeofuse',
    id: 'purposeofuse',
    type: 'select',
    options: [],
    isMultiple: false,
    md: 2,
    xs: 12,
  },
];

export const REQUEST_ROWS_PER_PAGE_OPTIONS = [100, 125, 150];
export const SUMMARY_ROWS_PER_PAGE_OPTIONS = [5, 15, 25];
export const JOB_SUMMARY_ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
